import React, {useEffect, useRef, useState} from "react";
import Tooltip from "@material-ui/core/Tooltip";
import moment from "moment";
import LongTextTooltiper from "../../../../Utils/LongTextTooltiper/LongTextTooltiper";
import {Draggable} from "react-beautiful-dnd";
import {getTillLate} from "../../../../Orders/OrdersGrid/TimeTillArrivalProgressbar/TimeTillArrivalProgressbar";
import "./style.scss";
import useInterval from "../../../../Utils/UseInterval";
import CloseIcon from "@material-ui/icons/CloseRounded";
import Fade from "@material-ui/core/Fade";

function getStyle(isDraggingFromOutside, style, snapshot) {
    if (!isDraggingFromOutside)
        return style;

    if (!snapshot.isDragging) return {};
    if (!snapshot.isDropAnimating) {
        return style;
    }

    return {
        ...style,
        // cannot be 0, but make it super tiny
        transitionDuration: `0.001s`
    };
}

export default function Stop({
                                 stop,
                                 index,
                                 onStopDoubleClick,
                                 onRemoveStopFromRouteClick,
                                 onStopClick,
                                 onStopHover,
                                 onStopHoverOut,
                                 isDraggingFromOutside,
                                 proposedDeliveryGroups,
                                 drag,
                                 isDragDisabled
                             }) {
    let isDragging = false;

    if (drag && drag.draggableId === `stop-${stop.order_id}`) {
        if (drag.destination === null ||
            (drag.destination && (drag.source.droppableId !== drag.destination.droppableId || drag.source.index !== drag.destination.index))) {
            isDragging = true;
        }
    }

    return (
        <Draggable draggableId={`stop-${stop.order_id}`} isDragDisabled={isDragDisabled} index={index}>
            {(provided, snapshot) => {
                return (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getStyle(isDraggingFromOutside, provided.draggableProps.style, snapshot)}
                    >
                        <StopItem stop={stop}
                                  index={index}
                                  proposedDeliveryGroups={proposedDeliveryGroups}
                                  onStopDoubleClick={onStopDoubleClick}
                                  onRemoveStopFromRouteClick={onRemoveStopFromRouteClick}
                                  onStopClick={onStopClick}
                                  onStopHover={onStopHover}
                                  onStopHoverOut={onStopHoverOut}
                                  isDragging={isDragging}
                        />
                    </div>
                );
            }}
        </Draggable>
    );
}

function StopItem(props) {
    const {stop, index, proposedDeliveryGroups} = props;

    const [latenessColor, setLatenessColor] = useState('white');
    const [isIndexVisible, setIndexVisibility] = useState(false);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const [stateIndex, setStateIndex] = useState("");
    const stopDoubleClickData = useRef(null);
    const stopDoubleClickClearTimer = useRef(null);
    const visibilityTimeoutRef = useRef(null);

    function setIndexAnimated(newIndex) {
        setIndexVisibility(false);

        if (visibilityTimeoutRef.current)
            clearTimeout(visibilityTimeoutRef.current);

        visibilityTimeoutRef.current = setTimeout(() => {
            setStateIndex(newIndex);
            setIndexVisibility(true);
            visibilityTimeoutRef.current = undefined;
        }, 200);
    }

    const updateLateness = () => {
        const tillLate = getTillLate(proposedDeliveryGroups, stop.order);

        if (tillLate) {
            if (tillLate.percentTillLate <= 50)
                setLatenessColor('ForestGreen');
            else if (tillLate.minutesTillLate > 5)
                setLatenessColor("#CCCC00");
            else
                setLatenessColor('Crimson');
        }
    };

    useEffect(() => {
        updateLateness();
    }, [stop, proposedDeliveryGroups]);

    useEffect(() => {
        if (props.isDragging)
            closeTooltip();
    }, [props.isDragging])

    useEffect(() => {
        if (index === stateIndex)
            return;

        if (stateIndex != null)
            setIndexAnimated(index);
        else {
            setStateIndex(index);
            setIndexVisibility(true);
        }
    }, [index]);

    useInterval(() => {
        updateLateness();
    }, 20000);

    const onStopHover = (e, stopId) => {
        if (props.onStopHover)
            props.onStopHover(e, stopId)
    };

    const onStopHoverOut = (e, stopId) => {
        if (props.onStopHoverOut)
            props.onStopHoverOut(e, stopId);
    };

    const onStopDoubleClick = (e, stopId) => {
        if (props.onStopDoubleClick)
            props.onStopDoubleClick(e, stopId);
    };

    const onStopSingleClick = (e, stopId) => {
        if (props.onStopClick)
            props.onStopClick(e, stopId);
    };

    const onRemoveStopFromRouteClick = (e, stopId) => {
        e.stopPropagation();

        if (props.onRemoveStopFromRouteClick)
            props.onRemoveStopFromRouteClick(e, stopId);
    };

    const onStopClick = (e, stopId) => {
        e.stopPropagation();

        if (stopDoubleClickData.current && stopDoubleClickData.current.stopId === stopId) {
            onStopDoubleClick(e, stopId);

            if (stopDoubleClickClearTimer.current)
                clearTimeout(stopDoubleClickClearTimer.current);

            stopDoubleClickData.current = undefined;
            stopDoubleClickClearTimer.current = undefined;
        } else {
            if (stopDoubleClickClearTimer.current)
                clearTimeout(stopDoubleClickClearTimer.current);

            stopDoubleClickData.current = {
                stopId: stopId
            };

            stopDoubleClickClearTimer.current = setTimeout(() => {
                stopDoubleClickData.current = undefined;
                stopDoubleClickClearTimer.current = undefined;

                onStopSingleClick(e, stopId);
            }, 300);
        }
    };

    function openTooltip() {
        setIsTooltipOpen(true);
    }

    function closeTooltip() {
        setIsTooltipOpen(false);
    }

    return (
        <Tooltip leaveTouchDelay={3000}
                 arrow
                 open={isTooltipOpen}
                 onOpen={openTooltip}
                 onClose={closeTooltip}
                 title={
                     <div className="stop-tooltip">
                         <div>
                             זמן מתחילת הנסיעה: {moment.duration(stop.duration, "seconds").humanize()}
                         </div>
                         <div>
                             זמן מקבלת מההזמנה: {moment.duration(stop.order_duration, "seconds").humanize()}
                         </div>
                     </div>
                 }>
            <div className="route-stop"
                 onClick={(e) => onStopClick(e, stop.order_id)}
                 onMouseOver={(e) => onStopHover(e, stop.order_id)}
                 onMouseOut={(e) => onStopHoverOut(e, stop.order_id)}>
                <div className="stop-index">
                    <div className="remove-stop-from-route-button"
                         onClick={(e) => onRemoveStopFromRouteClick(e, stop.order_id)}>
                        <CloseIcon fontSize="small"/>
                    </div>
                    <div className="stop-index-text">
                        <Fade timeout={250} in={isIndexVisible}>
                            <div>
                                {stateIndex + 1}
                            </div>
                        </Fade>
                    </div>
                    <div className="stop-index-background"
                         style={{
                             "backgroundColor": latenessColor
                         }}
                    />
                </div>
                <div className="stop-address">
                    <LongTextTooltiper backgroundColor="#f5f5f5">
                        {stop.order?.address}
                    </LongTextTooltiper>
                </div>
            </div>
        </Tooltip>
    );
}